import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
// The component now relies on this import for the actual API call.
import { fetchReservationSummary } from 'api';
import DottedLoader from 'components/loaders/DottedLoader'; 

// --- HELPER FUNCTION ---
const formatTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const seconds = Math.floor((now - past) / 1000);
    let interval = seconds / 86400; // days
    if (interval > 1.9) return `${Math.floor(interval)} days ago`;
    if (interval >= 1) return "Yesterday";
    interval = seconds / 3600; // hours
    if (interval >= 1) return `${Math.floor(interval)} hours ago`;
    interval = seconds / 60; // minutes
    if (interval >= 1) return `${Math.floor(interval)} minutes ago`;
    return "Just now";
};

// --- UI COMPONENTS ---

const AttendeeRow = React.memo(({ reservation }) => {
    const user = reservation.user || { firstName: 'Unknown', lastName: 'User', username: 'anonymous', imageUrl: null };
    const imageUrl = user.imageUrl || `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=ff6600&color=fff`;
    
    return (
        <div className="flex items-center justify-between p-2.5">
            <div className="flex items-center gap-3">
                <img src={imageUrl} alt={user.firstName} className="w-10 h-10 rounded-full object-cover border-2 border-white/20"/>
                <div>
                    <p className="font-semibold text-[#020102] dark:text-white">{`${user.firstName} ${user.lastName}`}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">@{user.username}</p>
                </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatTimeAgo(reservation.registeredAt)}</p>
        </div>
    );
});

const TeamRosterCard = ({ timeline }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const reservations = timeline.reservations || [];
    const VISIBLE_LIMIT = 4;
    const hasMore = reservations.length > VISIBLE_LIMIT;
    const reservationsToShow = isExpanded ? reservations : reservations.slice(0, VISIBLE_LIMIT);

    return (
        <div className="bg-white/70 dark:bg-[#1C2541]/80 backdrop-blur-sm rounded-xl shadow-md overflow-hidden transition-shadow hover:shadow-xl border border-black/10 dark:border-white/10">
            <div className="p-4 border-b border-black/10 dark:border-white/10">
                <div className="flex flex-wrap justify-between items-center gap-2">
                    <h4 className="text-xl font-bold text-[#020102] dark:text-white">{timeline.team?.name || 'Unknown Team'}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2"><FaUsers /><span>{reservations.length} Attendees</span></div>
                    </div>
                </div>
            </div>
            <div className="p-2">
                {reservationsToShow.length > 0 ? (
                    <motion.div initial="hidden" animate="visible" className="divide-y divide-black/5 dark:divide-white/5">
                        {reservationsToShow.map(res => (
                            <motion.div key={res.id} className="transition-colors hover:bg-black/5 dark:hover:bg-white/5 rounded-md">
                                <AttendeeRow reservation={res} />
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="text-center py-10"><p className="text-gray-500 dark:text-gray-400">No reservations from this team yet.</p></div>
                )}
                {hasMore && (
                    <button onClick={() => setIsExpanded(!isExpanded)} className="w-full text-center text-sm font-semibold text-[#ff6600] hover:underline p-2 rounded-lg">
                        {isExpanded ? 'Show less' : `Show ${reservations.length - VISIBLE_LIMIT} more...`}
                    </button>
                )}
            </div>
        </div>
    );
};

const EventDetails = ({ event }) => {
    const timelines = event.Timelines || [];
    const sortedTimelines = [...timelines].sort((a, b) => {
        const aCount = a.reservations?.length || 0;
        const bCount = b.reservations?.length || 0;
        return bCount - aCount;
    });

    // Corrected: Uses lowercase `reservations` to match backend alias
    const totalAttendees = timelines.reduce((sum, tl) => sum + (tl.reservations?.length || 0), 0);
    const headerImage = event.headerImage || `https://placehold.co/1200x400/020102/FFFFFF?text=${event.title.replace(/\s/g, '+')}`;

    return (
        <div className="bg-white dark:bg-[#1C2541] rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-8 text-white relative bg-[#020102]">
                <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{backgroundImage: `url(${headerImage})`}}></div>
                <div className="absolute inset-0 bg-gradient-to-br from-[#ff6600] to-amber-500 opacity-80"></div>
                <div className="relative z-10">
                    <h2 className="text-4xl font-bold mb-2 text-shadow">{event.title}</h2>
                    <p className="text-white/90 mb-6 max-w-3xl">{event.description}</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
                        <div className="flex items-center gap-2"><FaCalendarAlt /><span>{new Date(event.startDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                        <div className="flex items-center gap-2"><FaClock /><span>{event.hours}</span></div>
                        <div className="flex items-center gap-2"><FaMapMarkerAlt /><span>{event.location}</span></div>
                    </div>
                </div>
            </div>
            <div className="p-6 md:p-8 bg-gray-50 dark:bg-[#0B132B]">
                <div className="flex items-center gap-3 mb-8">
                    <FaUsers className="text-3xl text-[#ff6600]" />
                    <h3 className="text-3xl font-bold text-[#020102] dark:text-white">
                        {totalAttendees}<span className="text-gray-500 dark:text-gray-400 font-normal text-2xl ml-2">Attendees</span>
                    </h3>
                </div>
                <div className="space-y-6">
                    {sortedTimelines.map(timeline => <TeamRosterCard key={timeline.id} timeline={timeline} />)}
                </div>
            </div>
        </div>
    );
};

const EventButton = React.memo(({ event, isSelected, onClick }) => {
    // Corrected: Uses lowercase `reservations` to match backend alias
    const attendeeCount = (event.Timelines || []).reduce((sum, tl) => sum + (tl.reservations?.length || 0), 0);
    return (
        <button
            onClick={onClick}
            className={`w-full text-left p-4 rounded-lg transition-all duration-300 transform ${isSelected ? 'bg-[#ff6600] text-white shadow-lg -translate-y-1' : 'hover:bg-gray-100 dark:hover:bg-[#0B132B]/50 hover:shadow-md text-[#020102] dark:text-gray-200'}`}
        >
            <div className="flex justify-between items-start">
                <p className="font-bold pr-2">{event.title}</p>
                <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${isSelected ? 'bg-black/20' : 'bg-gray-200 dark:bg-[#0B132B]'}`}>
                    <FaUsers/>
                    <span>{attendeeCount}</span>
                </div>
            </div>
            <p className={`text-sm mt-1 ${isSelected ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                {new Date(event.startDate).toLocaleDateString()}
            </p>
        </button>
    );
});


// --- MAIN COMPONENT ---
const Reservations = () => {
    const { data: events, isLoading, error } = useQuery({
        queryKey: ['reservationSummary'],
        queryFn: fetchReservationSummary, // This now calls your real API function
        staleTime: 5 * 60 * 1000, 
    });

    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        if (events && events.length > 0 && !selectedEvent) {
            setSelectedEvent(events[0]);
        }
    }, [events, selectedEvent]);

    if (isLoading) {
        return <DottedLoader text="Loading reservations..." />;
    }

    if (error) {
        return (
             <div className="flex flex-col items-center justify-center h-96 text-red-500 bg-red-500/10 rounded-lg p-4">
                <h3 className="text-2xl font-bold">An Error Occurred</h3>
                <p className="mt-2 text-center">{error.message || 'Failed to load reservation data.'}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-1/3 xl:w-1/4">
                <div className="bg-white dark:bg-[#1C2541] p-4 rounded-2xl shadow-lg">
                    <h3 className="text-xl font-bold text-[#020102] dark:text-white p-2 mb-2">Events</h3>
                    <div className="space-y-2">
                        {(events || []).map(event => (
                            <EventButton key={event.id} event={event} isSelected={selectedEvent?.id === event.id} onClick={() => setSelectedEvent(event)}/>
                        ))}
                    </div>
                </div>
            </aside>
            <section className="w-full lg:w-2/3 xl:w-3/4">
                <AnimatePresence mode="wait">
                    <motion.div key={selectedEvent ? selectedEvent.id : 'empty'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                        {(selectedEvent && events?.length > 0) ? (
                            <EventDetails event={selectedEvent} />
                        ) : (
                            <div className="flex items-center justify-center h-full bg-white dark:bg-[#1C2541] rounded-2xl shadow-lg p-10 text-center">
                                <div>
                                    <FaCalendarAlt className="mx-auto text-6xl text-gray-300 dark:text-gray-700 mb-4" />
                                    <h3 className="text-2xl font-bold text-[#020102] dark:text-white">No Events Found</h3>
                                    <p className="text-gray-500 dark:text-gray-400 mt-2">There are currently no events with reservations to display.</p>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </section>
        </div>
    );
};

export default Reservations;
